
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MessageFormValues, AutomatedMessage } from "@/components/cashback/message-utils/types";
import { ModuleType } from "./types";

export const useCreateMessageMutation = (moduleType: ModuleType, userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: MessageFormValues) => {
      if (!userId) throw new Error('User not authenticated');

      const { data: message, error } = await supabase
        .from('automated_messages')
        .insert({
          user_id: userId,
          module_type: moduleType,
          send_time: values.sendTime,
          message: values.message,
          channels: values.channels,
          is_active: values.isActive !== undefined ? values.isActive : true,
          sms_template_id: values.smsTemplateId || null
        })
        .select()
        .single();

      if (error) throw error;

      // Create default metrics for each channel
      if (values.channels.length > 0) {
        const metricsToInsert = values.channels.map(channel => ({
          message_id: message.id,
          channel,
          open_rate: 0,
          click_rate: 0,
          conversion_rate: 0,
          sent_count: 0
        }));

        const { error: metricsError } = await supabase
          .from('automated_message_metrics')
          .insert(metricsToInsert);

        if (metricsError) {
          console.error('Error creating metrics:', metricsError);
        }
      }

      return message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automated-messages', moduleType] });
      toast({
        title: "Mensagem criada",
        description: "A mensagem automática foi criada com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error creating message:', error);
      toast({
        title: "Erro ao criar mensagem",
        description: "Não foi possível criar a mensagem. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateMessageMutation = (moduleType: ModuleType, userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: MessageFormValues }) => {
      if (!userId) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('automated_messages')
        .update({
          send_time: values.sendTime,
          message: values.message,
          channels: values.channels,
          is_active: values.isActive !== undefined ? values.isActive : true,
          sms_template_id: values.smsTemplateId || null
        })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      // Update metrics - remove old ones and add new ones for channels
      await supabase
        .from('automated_message_metrics')
        .delete()
        .eq('message_id', id);

      if (values.channels.length > 0) {
        const metricsToInsert = values.channels.map(channel => ({
          message_id: id,
          channel,
          open_rate: 0,
          click_rate: 0,
          conversion_rate: 0,
          sent_count: 0
        }));

        const { error: metricsError } = await supabase
          .from('automated_message_metrics')
          .insert(metricsToInsert);

        if (metricsError) {
          console.error('Error updating metrics:', metricsError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automated-messages', moduleType] });
      toast({
        title: "Mensagem atualizada",
        description: "A mensagem automática foi atualizada com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error updating message:', error);
      toast({
        title: "Erro ao atualizar mensagem",
        description: "Não foi possível atualizar a mensagem. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteMessageMutation = (moduleType: ModuleType, userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!userId) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('automated_messages')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automated-messages', moduleType] });
      toast({
        title: "Mensagem excluída",
        description: "A mensagem automática foi excluída com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error deleting message:', error);
      toast({
        title: "Erro ao excluir mensagem",
        description: "Não foi possível excluir a mensagem. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};

export const useToggleActiveMutation = (moduleType: ModuleType, messages: AutomatedMessage[], userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!userId) throw new Error('User not authenticated');

      const message = messages.find(m => m.id === id);
      if (!message) throw new Error('Message not found');

      const { error } = await supabase
        .from('automated_messages')
        .update({ is_active: !message.isActive })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automated-messages', moduleType] });
      toast({
        title: "Status alterado",
        description: "O status da mensagem foi alterado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error toggling message status:', error);
      toast({
        title: "Erro ao alterar status",
        description: "Não foi possível alterar o status da mensagem. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};
